import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';
import UbicacionPicker from '../components/UbicacionPicker';

const EMPTY_FORM = {
    centro_poblado_id: '',
    cod_divipola: '',
    nombre: '',
    ciudad: '',
    departamento_id: '',
    municipio_id: '',
    nom_departamento: '',
    nom_municipio: '',
    activa: true
};

const Zonas = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [ubicacionSeleccionada, setUbicacionSeleccionada] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/zonas?all=1');
            setItems(data);
        } catch {
            setError('Error cargando zonas');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setUbicacionSeleccionada(null);
        setEditingId(null);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            centro_poblado_id: item.centro_poblado_id || '',
            cod_divipola: item.cod_divipola || '',
            nombre: item.nombre,
            ciudad: item.ciudad,
            departamento_id: item.departamento_id || '',
            municipio_id: item.municipio_id || '',
            nom_departamento: item.nom_departamento || '',
            nom_municipio: item.nom_municipio || '',
            activa: !!Number(item.activa)
        });
        setUbicacionSeleccionada(item.cod_divipola ? { codigo: item.cod_divipola, nombre: item.nombre } : null);
        setEditingId(item.id);
        setError('');
        setModalOpen(true);
    };

    const onUbicacion = (cp, depto, muni) => {
        if (!cp) { setUbicacionSeleccionada(null); return; }
        const nombreZona = `${cp.codigo} — ${cp.nombre}`;
        setUbicacionSeleccionada({ codigo: cp.codigo, nombre: cp.nombre });
        setForm(prev => ({
            ...prev,
            centro_poblado_id: cp.id,
            cod_divipola: cp.codigo,
            nombre: nombreZona,
            ciudad: muni?.nombre || '',
            departamento_id: depto?.id || '',
            municipio_id: muni?.id || '',
            nom_departamento: depto?.nombre || '',
            nom_municipio: muni?.nombre || ''
        }));
    };

    const handleSubmit = async () => {
        if (!form.cod_divipola) {
            setError('Debe seleccionar un centro poblado DIVIPOLA');
            return;
        }
        setSaving(true);
        setError('');
        try {
            // Usar cod_divipola como código interno
            const payload = { ...form, codigo: form.cod_divipola };
            if (editingId) {
                await api.put(`/zonas/${editingId}`, payload);
                showSuccess('Zona actualizada correctamente');
            } else {
                await api.post('/zonas', payload);
                showSuccess('Zona creada correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando zona');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta zona? Solo es posible si no tiene planes ni contratos activos.')) return;
        try {
            await api.delete(`/zonas/${id}`);
            showSuccess('Zona eliminada');
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'No se puede eliminar esta zona');
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Zonas</h2>
                {can('zonas', 'create') && (
                    <button onClick={openCreate}
                        className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
                        style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Agregar Nueva
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No hay zonas registradas</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Nombre / DIVIPOLA</th>
                                <th className="px-4 py-3 text-left">Departamento</th>
                                <th className="px-4 py-3 text-left">Municipio</th>
                                <th className="px-4 py-3 text-left">Estado</th>
                                {can('zonas', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-gray-800">{item.nombre}</p>
                                        {item.cod_divipola && (
                                            <p className="text-xs font-mono text-gray-400">{item.cod_divipola}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.nom_departamento || item.ciudad}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.nom_municipio || '—'}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${Number(item.activa) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {Number(item.activa) ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    {can('zonas', 'update') && (
                                        <td className="px-4 py-3 text-right flex gap-2 justify-end">
                                            <button onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                Editar
                                            </button>
                                            <button onClick={() => handleDelete(item.id)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50">
                                                Eliminar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar zona' : 'Nueva zona'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div className="border border-gray-200 rounded-xl p-4">
                                <p className="text-sm font-semibold text-gray-700 mb-3">📍 Seleccionar ubicación DIVIPOLA</p>
                                <UbicacionPicker onChange={onUbicacion} />
                            </div>

                            {ubicacionSeleccionada && (
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                    <p className="text-xs text-blue-500 font-medium mb-1">Zona que se creará:</p>
                                    <p className="text-lg font-bold text-blue-800">{form.nombre}</p>
                                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-blue-600">
                                        <span>🏛 {form.nom_departamento}</span>
                                        <span>🏙 {form.nom_municipio}</span>
                                    </div>
                                </div>
                            )}

                            {editingId && (
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" id="zona_activa" checked={form.activa}
                                        onChange={e => setForm(prev => ({ ...prev, activa: e.target.checked }))} />
                                    <label htmlFor="zona_activa" className="text-sm text-gray-700">Zona activa</label>
                                </div>
                            )}
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                Cancelar
                            </button>
                            <button onClick={handleSubmit} disabled={saving || !ubicacionSeleccionada}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-40"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear zona')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Zonas;
